import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isVerifying, setIsVerifying] = useState(true);
  const { syncWithStripe, isSubscribed, isLoading } = useSubscriptionStatus();

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const verifySubscription = async () => {
      if (!sessionId) {
        toast.error('No session ID found');
        navigate('/app/subscribe');
        return;
      }

      try {
        await syncWithStripe();
        setTimeout(() => setIsVerifying(false), 2000);
      } catch (error) {
        console.error('Error verifying subscription:', error);
        toast.error('Error verifying subscription');
        setIsVerifying(false);
      }
    };

    verifySubscription();
  }, [sessionId, syncWithStripe, navigate]);

  useEffect(() => {
    if (!isVerifying && !isLoading && isSubscribed) {
      setTimeout(() => navigate('/app/emails'), 3000);
    }
  }, [isVerifying, isLoading, isSubscribed, navigate]);

  if (isVerifying || isLoading) {
    return (
      <div className="min-h-screen bg-[#faf9f7] dark:bg-[#0a0a0a] flex items-center justify-center px-6">
        <div className="text-center">
          <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-700 border-t-[#f97316] rounded-full animate-spin mx-auto mb-6" />
          <h2 className="text-[1.25rem] font-light text-gray-900 dark:text-white mb-2">
            Confirming your subscription
          </h2>
          <p className="text-[14px] text-gray-400 dark:text-gray-500">
            Just a moment...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf9f7] dark:bg-[#0a0a0a] flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
        className="w-full max-w-[400px] text-center"
      >
        {/* Checkmark */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.4 }}
          className="w-12 h-12 mx-auto mb-8 rounded-full bg-[#f97316] flex items-center justify-center"
        >
          <motion.svg
            className="w-6 h-6 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <motion.path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            />
          </motion.svg>
        </motion.div>

        <h1 className="text-[1.75rem] sm:text-[2rem] font-light tracking-tight text-gray-900 dark:text-white mb-3">
          You're subscribed
        </h1>

        <p className="text-[15px] text-gray-400 dark:text-gray-500 leading-relaxed mb-10 max-w-xs mx-auto">
          Your account is active. You have access to everything.
        </p>

        {/* Includes */}
        <div className="mb-10 text-left">
          <p className="text-[12px] tracking-wider uppercase text-gray-400 dark:text-gray-600 mb-3 px-1">Includes</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 px-1">
            {['AI assistant', 'Invoicing', 'Contracts', 'Client portal', 'Lead follow-ups', 'Scheduling'].map((feature) => (
              <span key={feature} className="text-[13px] text-gray-500 dark:text-gray-400">{feature}</span>
            ))}
          </div>
        </div>

        <button
          onClick={() => navigate('/app/emails')}
          className="w-full py-4 bg-[#f97316] hover:bg-[#ea580c] text-white text-[15px] font-medium rounded-xl transition-colors"
        >
          Enter your dashboard
        </button>

        <p className="text-[12px] text-gray-400 dark:text-gray-600 mt-4">
          Redirecting automatically...
        </p>
      </motion.div>
    </div>
  );
};

export default SubscriptionSuccess;
